/**
 * Relit ce que l'enregistreur a écrit : le manifeste, les gestes, les états de
 * séance, les participants.
 *
 * # Le format est celui de l'enregistreur, à l'octet près
 *
 * `docs/spec/recorder.md` en est le contrat, et le paquet Go
 * `live/internal/recorder/journal` l'écrit. Ce qui se lit ici doit donc suivre
 * ce qui s'écrit là-bas : gros-boutiste, en-tête `VRTN`, un enregistrement
 * d'identité par émetteur puis des paquets qui la désignent par son index.
 *
 * # Un journal tronqué se lit quand même
 *
 * Les journaux ne font qu'ajouter, et un arrêt brutal peut laisser un
 * enregistrement à moitié écrit **en fin de fichier**. On s'arrête là, sans
 * rien lever : ce qui précède est intact, et c'est un récital de moins une
 * demi-seconde plutôt qu'un replay de rien. Un enregistrement impossible
 * ailleurs qu'à la fin, lui, est une corruption, et lève.
 */

/** L'en-tête d'un journal de gestes : « VRTN », puis sa version. */
const NOTES_MAGIC = [0x56, 0x52, 0x54, 0x4e];
const NOTES_VERSION = 1;

/** Les genres d'enregistrement d'un journal de gestes. */
const RECORD_IDENTITY = 0x49; // 'I'
const RECORD_PACKET = 0x50; // 'P'

/** Un paquet du canal de jeu, tel que la salle l'a livré. */
export interface ReplayPacket {
  /** Instant de réception, en millisecondes Unix sur l'horloge du service. */
  receivedAtMs: number;
  /** L'identité LiveKit de l'émetteur. */
  from: string;
  payload: Uint8Array<ArrayBuffer>;
}

/** Une métadonnée de salle reçue, telle que le service l'avait écrite. */
export interface ReplayStateLine {
  receivedAtMs: number;
  metadata: string;
}

/** Une arrivée, un changement ou un départ. */
export interface ReplayParticipantLine {
  atMs: number;
  event: "joined" | "updated" | "left";
  identity: string;
  name?: string;
  attributes?: Record<string, string>;
  canPublishData: boolean;
}

/** Une jointure de l'enregistreur : ses journaux, et rien entre deux. */
export interface ReplayManifestPart {
  number: number;
  startedAtMs: number;
  endedAtMs?: number;
  /**
   * Le journal des gestes de la jointure entière.
   *
   * **Absent d'un dépôt découpé** (format 3, depuis le 2026-09-22) : les gestes
   * vivent alors dans le journal de chaque prise (`ReplayManifestTake.notes`),
   * et celui-ci n'est pas déposé — il porterait les essais qu'on n'a pas
   * publiés.
   */
  notes?: string;
  packets: number;
  state: string;
  stateChanges: number;
  participants: string;
}


export interface ReplayManifestTrack {
  file: string;
  part: number;
  kind: string;
  codec: string;
  /**
   * `webm` ou `mp4`. Absent des enregistrements déposés avant le 2026-09-19,
   * où tout était du WebM. Le lecteur n'en a pas besoin — un `<video>` suit le
   * type que l'API rend —, il est là pour qui relit un manifeste.
   */
  container?: string;
  /**
   * La prise que ce fichier porte, et elle seule (format 3). Absent quand le
   * dépôt n'est pas découpé : le fichier porte alors toute la séance.
   */
  take?: number;
  trackSid: string;
  /**
   * Le nom que l'émetteur a donné à sa piste. Plus lu : ce que filmait une
   * caméra se lit dans la séance (`replay-subjects.ts`), et les pistes partent
   * désormais sans nom.
   */
  trackName?: string;
  participant: string;
  originMs: number;
  durationMs: number;
  frames: number;
  /**
   * La définition des images du fichier, quand l'enregistreur l'a écrite — en
   * MP4, où un fichier ne porte qu'une définition. Une caméra qui en change
   * donne donc plusieurs fichiers : voir `replay-cameras.ts`.
   */
  width?: number;
  height?: number;
  /** Ce qui pose ce fichier sur la séance, quand l'enregistreur l'a écrit. */
  sync?: ReplayTrackSync[];
}

export interface ReplayManifest {
  format: number;
  room: string;
  id: string;
  label?: string;
  createdAtMs: number;
  endedAtMs?: number;
  stopReason?: string;
  degraded?: boolean;
  parts: ReplayManifestPart[];
  participants: {
    identity: string;
    name?: string;
    role: string;
    firstSeenMs: number;
    lastSeenMs: number;
  }[];
  tracks: ReplayManifestTrack[];
  /**
   * Les prises, quand l'interprète a découpé son enregistrement (version 2 du
   * manifeste). Absentes d'un enregistrement d'une seule traversée, et de tout
   * ce qui a été déposé avant le signal de prise.
   */
  takes?: ReplayManifestTake[];
}

/**
 * Une prise, telle que l'enregistreur l'a bornée.
 *
 * Elle vient d'un **fait** — le compteur que l'interprète incrémente pendant
 * qu'il joue —, pas d'une devinette. `startTick`/`endTick` manquent quand la
 * prise ne porte aucun geste daté ; les positions, elles, sont toujours là.
 */
export interface ReplayManifestTake {
  number: number;
  parts?: number[];
  /**
   * Les journaux de gestes de cette prise, un par jointure traversée (format 3,
   * depuis le 2026-09-22). C'est de là que le lecteur rebâtit ce qu'elle a
   * joué.
   *
   * Absent d'un dépôt d'avant le découpage : les gestes sont alors dans
   * `ReplayManifestPart.notes`, mêlés à ceux des autres prises.
   */
  notes?: string[];
  /**
   * Le fichier MIDI de la prise dans le dépôt, écrit par l'enregistreur —
   * absent quand elle ne porte aucune note. Ses ticks repartent de zéro, à
   * la division et au tempo de la séance.
   */
  midi?: string;
  startedAtMs: number;
  endedAtMs?: number;
  startPositionMs: number;
  endPositionMs: number;
  packets?: number;
  gestures?: number;
  startTick?: number;
  endTick?: number;
}

/**
 * Un point de calage d'une piste sur la séance (version 2 du manifeste).
 *
 * Dans `[fromFileMs, toFileMs]`, la position de séance vaut
 * `positionMs + (t − fileMs)`. Le trou entre deux points est ce que la séance
 * n'a pas avancé — une pause, un déplacement de tête de lecture.
 */
export interface ReplayTrackSync {
  fromFileMs: number;
  toFileMs: number;
  fileMs: number;
  positionMs: number;
  tick?: number;
}

/** Ce qu'on refuse d'appeler un enregistrement. */
export class ReplayFormatError extends Error {}

/**
 * Relit un journal de gestes.
 *
 * Les paquets sortent dans l'ordre du fichier, qui est celui de leur arrivée.
 */
export function readNotesJournal(bytes: ArrayBuffer): ReplayPacket[] {
  const view = new DataView(bytes);
  const all = new Uint8Array(bytes);

  if (all.length < 6 || NOTES_MAGIC.some((byte, index) => all[index] !== byte)) {
    throw new ReplayFormatError("Ce fichier n'est pas un journal de gestes.");
  }

  const version = view.getUint16(4, false);

  if (version !== NOTES_VERSION) {
    throw new ReplayFormatError(`Journal de gestes en version ${version}, attendue ${NOTES_VERSION}.`);
  }

  const packets: ReplayPacket[] = [];
  const identities: string[] = [];
  const decoder = new TextDecoder();

  let at = 6;

  while (at < all.length) {
    const kind = all[at];

    at += 1;

    if (kind === RECORD_IDENTITY) {
      // index (2), longueur (2), octets
      if (at + 4 > all.length) {
        break;
      }

      const index = view.getUint16(at, false);
      const length = view.getUint16(at + 2, false);

      at += 4;

      if (at + length > all.length) {
        break;
      }

      if (index !== identities.length) {
        throw new ReplayFormatError("Journal de gestes corrompu : une identité manque.");
      }

      identities.push(decoder.decode(all.subarray(at, at + length)));
      at += length;

      continue;
    }

    if (kind !== RECORD_PACKET) {
      throw new ReplayFormatError("Journal de gestes corrompu : enregistrement inconnu.");
    }

    // index (2), reçu (8), longueur (2), octets
    if (at + 12 > all.length) {
      break;
    }

    const index = view.getUint16(at, false);
    const receivedAtMs = view.getFloat64(at + 2, false);
    const length = view.getUint16(at + 10, false);

    at += 12;

    if (at + length > all.length) {
      break;
    }

    const from = identities[index];

    if (from === undefined) {
      throw new ReplayFormatError("Journal de gestes corrompu : identité inconnue.");
    }

    // Une copie, et non une vue : le moteur garde ce qu'on lui donne, et une
    // vue partagée sur tout le fichier retiendrait le fichier entier.
    packets.push({ receivedAtMs, from, payload: all.slice(at, at + length) });
    at += length;
  }

  return packets;
}

/**
 * Relit un journal de lignes JSON.
 *
 * Une ligne illisible est sautée : c'est la dernière d'un fichier tronqué, ou
 * une ligne qu'une version future aura écrite autrement.
 */
export function readJsonLines<T>(text: string): T[] {
  const out: T[] = [];

  for (const line of text.split("\n")) {
    const trimmed = line.trim();

    if (trimmed === "") {
      continue;
    }

    try {
      out.push(JSON.parse(trimmed) as T);
    } catch {
      // Ligne tronquée ou inconnue : on garde ce qui précède.
    }
  }

  return out;
}

/** Relit un manifeste, en vérifiant ce dont le lecteur dépend. */
export function readManifest(body: unknown): ReplayManifest {
  if (typeof body !== "object" || body === null) {
    throw new ReplayFormatError("Manifeste illisible.");
  }

  const manifest = body as ReplayManifest;

  /*
    Les versions que ce lecteur sait lire.

    2 ajoute les prises et le calage des pistes ; 3 découpe le dépôt par prise —
    un fichier de caméra et un journal de gestes chacune, et plus de journal de
    jointure. Rien n'a changé de sens en chemin : ce lecteur lit les trois, et
    ignore ce qu'il ne connaît pas. La borne existe pour la version qui
    *retirerait* quelque chose dont il dépend — ce que 3 fait, justement, et
    c'est pourquoi il a fallu l'y inscrire.
  */
  if (manifest.format < 1 || manifest.format > 3) {
    throw new ReplayFormatError(`Manifeste en format ${String(manifest.format)}, attendu 1 à 3.`);
  }

  if (!Array.isArray(manifest.parts) || manifest.parts.length === 0) {
    throw new ReplayFormatError("Manifeste sans jointure : il n'y a rien à rejouer.");
  }

  return manifest;
}
